import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import Loader from "@/components/Loader";
import EmptyState from "@/components/EmptyState";

const TYPE_COLORS = {
  "Concert": "#F5ACB4", "Animation": "#FEC900", "Spectacle": "#5BC5F2", "Atelier": "#A1C861",
};

export default function Programme() {
  const [data, setData] = useState(null);
  const [day, setDay] = useState("all");
  const [type, setType] = useState("all");

  useEffect(() => {
    api.get("/programme").then(({ data }) => setData(data)).catch(() => setData([]));
  }, []);

  const days = useMemo(() => {
    if (!data) return [];
    return Array.from(new Set(data.map((p) => p.date).filter(Boolean)));
  }, [data]);
  const types = useMemo(() => {
    if (!data) return [];
    return Array.from(new Set(data.map((p) => p.type || p.category).filter(Boolean)));
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.filter((p) => (day === "all" || p.date === day) && (type === "all" || (p.type || p.category) === type));
  }, [data, day, type]);

  const groupedByDay = useMemo(() => {
    const g = {};
    filtered.forEach((p) => { const k = p.date || "Sans date"; (g[k] ||= []).push(p); });
    return g;
  }, [filtered]);

  if (!data) return <Loader label="Chargement du programme…" />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl font-black gradient-text inline-block">Programme</h1>
          <p className="text-neutral-400 mt-2">Concerts, animations, spectacles et bien plus — sur deux jours de fête.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={day} onChange={(e) => setDay(e.target.value)} className="bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2 text-sm" data-testid="filter-day">
            <option value="all">Tous les jours</option>
            {days.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={type} onChange={(e) => setType(e.target.value)} className="bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2 text-sm" data-testid="filter-type">
            <option value="all">Tous types</option>
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="Aucun événement" subtitle="Ajustez les filtres pour voir plus." />
      ) : (
        Object.entries(groupedByDay).map(([d, items]) => (
          <section key={d} className="mb-10">
            <h2 className="text-xl font-black text-neutral-200 mb-4">✨ {d}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((p) => {
                const t = p.type || p.category;
                const color = p.colorHex || TYPE_COLORS[t] || "#A1C861";
                return (
                  <div key={p.id} className="card-hsg p-5 hover:-translate-y-1 transition-transform">
                    <div className="chip mb-2" style={{ background: color + "22", color, border: `1px solid ${color}55` }}>{t}</div>
                    <h3 className="font-bold text-lg">{p.title}</h3>
                    {p.subtitle && <p className="text-neutral-400 text-sm mt-1">{p.subtitle}</p>}
                    <div className="mt-3 text-sm space-y-1 text-neutral-300">
                      {p.time && <div>🕒 {p.time}</div>}
                      {p.location && <div>📍 {p.location}</div>}
                      {p.age && <div>👦 {p.age}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
